import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiArrowRight, FiCheck } = FiIcons;

const HeroSection = ({ onBookPilot, onDemoVideo }) => {
  return (
    <section className="pt-20 lg:pt-32 pb-16 lg:pb-24 gradient-bg overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            className="text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="font-display font-bold text-4xl lg:text-6xl text-white leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Hire{' '}
              <span className="text-gradient bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                10× faster
              </span>
              . Shortlist smarter. Interview with AI confidence.
            </motion.h1>

            <motion.p
              className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Publish jobs, auto-shortlist candidates, run AI-backed video interviews, 
              and make unbiased hiring decisions — all from one elegant dashboard.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.button
                onClick={onBookPilot}
                className="gradient-gold text-slate-900 font-semibold px-8 py-4 rounded-lg shadow-elegant hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Book Free Pilot</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.button>

              <motion.button
                onClick={onDemoVideo}
                className="bg-transparent border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/10 transition-all duration-300 flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiPlay} className="w-5 h-5" />
                <span>See Live Demo</span>
              </motion.button>
            </motion.div>

            <motion.div
              className="text-sm text-slate-400 flex items-center justify-center lg:justify-start space-x-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-400" />
                <span>No credit card</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-400" />
                <span>14-day pilot</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-400" />
                <span>Quick onboarding</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Product Demo */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="relative bg-white rounded-2xl shadow-2xl p-6">
              {/* Mock Dashboard */}
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between border-b pb-4">
                  <h3 className="font-semibold text-slate-900">Dashboard Overview</h3>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-indigo-600">124</div>
                    <div className="text-sm text-slate-600">Active Jobs</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-600">2.3k</div>
                    <div className="text-sm text-slate-600">Candidates</div>
                  </div>
                </div>

                {/* Job List */}
                <div className="space-y-3">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <div className="w-4 h-4 bg-indigo-600 rounded"></div>
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 text-sm">Senior Developer</div>
                          <div className="text-xs text-slate-500">45 applications</div>
                        </div>
                      </div>
                      <div className="text-xs text-green-600 font-medium">87% match</div>
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                <button className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors">
                  View All Shortlists
                </button>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              className="absolute -top-4 -right-4 bg-green-500 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              ✓ AI Scored
            </motion.div>

            <motion.div
              className="absolute -bottom-4 -left-4 bg-yellow-500 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1 }}
            >
              🎥 Interview Ready
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;